import {
  adminGigsterProfilesList,
  adminGigsterProfilesW2EmploymentStatusCreate,
  adminGigsterProfilesW2EmploymentStatusRetrieve,
  adminVerificationsCreate,
  adminVerificationsUpdate,
  customFetch,
  getAdminGigsterProfilesListQueryKey,
  type ProCertificationCreate,
  proCertificationsCreate,
  type ProfileHeader,
  proCertificationsPartialUpdate,
  usersRetrieve,
  type W2EmploymentStatusUpdateStatusEnum,
  type W2TerminationImpact,
} from "@sidegigllc/api/client";
import type { ApiError } from "@sidegigllc/api/resources";
import { queryOptions, skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AdminProUser } from "api/resources/admin";
import type { SetOptional } from "type-fest";
import type { MutationHookWithParams, QueryHook, QueryOptions } from "./types";

const ADMIN_PROS_UPDATE = "admin_pros_UPDATE";

export const adminProQueryOptions = (id?: number, options?: QueryOptions<AdminProUser>) =>
  queryOptions<AdminProUser, ApiError, AdminProUser>({
    queryKey: ["admin_pros_GET", id],
    queryFn:
      id == null
        ? skipToken
        : async (): Promise<AdminProUser> => {
            const [proUser, proProfile] = await Promise.all([
              usersRetrieve(id),
              adminGigsterProfilesList({ user_id: id }),
            ]);
            // `usersRetrieve` is polymorphic: both gigster variants carry `gigster_profile`, the
            // `User` variant only `gigster_profile_id`. Narrow (no cast) before merging on top.
            if (!("gigster_profile" in proUser)) throw new Error("Expected gigster response");

            return { ...proUser, ...proProfile.results[0] };
          },
    ...options,
  });

export const useAdminPro: QueryHook<AdminProUser, { id?: number }> = ({ id }, options) =>
  useQuery(adminProQueryOptions(id, options));

type UpdateVerificationVariables = { id?: number | null; pro_id: number; verified: boolean };

export const useUpdateVerification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ADMIN_PROS_UPDATE],
    mutationFn: ({ id, pro_id, verified }: UpdateVerificationVariables) =>
      id == null ? adminVerificationsCreate({ pro_id, verified }) : adminVerificationsUpdate(id, { verified }),
    onSuccess: (_data, { pro_id }) => {
      queryClient.invalidateQueries({ queryKey: getAdminGigsterProfilesListQueryKey() });
      queryClient.invalidateQueries({ queryKey: adminProQueryOptions(pro_id).queryKey });
    },
  });
};

// The generated section call types `header` as the all-sections wrapper; it returns the `ProfileHeader` itself.
// Nested under the pro's key so invalidating the pro refetches it.
const adminProHeaderQueryOptions = (id: number) =>
  queryOptions<ProfileHeader, ApiError>({
    queryKey: [...adminProQueryOptions(id).queryKey, "header"],
    queryFn: () => customFetch<ProfileHeader>(`/api/admin/gigster-profiles/${id}/header/`, { method: "GET" }),
  });

export const useAdminProW2EmploymentStatus = (id: number) =>
  useQuery({ ...adminProHeaderQueryOptions(id), select: (header) => header.w2_employment_status ?? null });

// Kept out of the pro's key: invalidating the pro after a terminate must not refetch the still-open preview.
const w2TerminationImpactQueryOptions = (id: number) =>
  queryOptions<W2TerminationImpact, ApiError>({
    queryKey: ["admin_pros_w2_termination_impact", id],
    queryFn: ({ signal }) => adminGigsterProfilesW2EmploymentStatusRetrieve(id, { signal }),
    // The admin confirms a termination against this list, so never treat a cached copy as current.
    staleTime: 0,
  });

export const useW2TerminationImpact = (id: number, { enabled }: { enabled: boolean }) =>
  useQuery({ ...w2TerminationImpactQueryOptions(id), enabled });

export const w2EmploymentStatusMutationKey = (proId: number) => [ADMIN_PROS_UPDATE, "w2_employment_status", proId];

/** Terminates or restores the pro's Zeal employment. Both shift lists are empty for a restore. */
export const useUpdateW2EmploymentStatus = (proId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: w2EmploymentStatusMutationKey(proId),
    mutationFn: (status: W2EmploymentStatusUpdateStatusEnum) =>
      adminGigsterProfilesW2EmploymentStatusCreate(proId, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminProQueryOptions(proId).queryKey }),
  });
};

export const useUpdateCertification: MutationHookWithParams<
  Partial<ProCertificationCreate>,
  SetOptional<Pick<ProCertificationCreate, "id" | "status" | "certificate_id" | "pro_id">, "id">
> = (options) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ADMIN_PROS_UPDATE],
    mutationFn: async (certification) => {
      if (certification.id) {
        const res = await proCertificationsPartialUpdate(certification.id, { status: certification.status });
        return { ...certification, ...res };
      }
      if (certification.certificate_id == null || certification.pro_id == null) {
        throw new Error("certificate_id and pro_id are required to create a certification");
      }
      return await proCertificationsCreate({
        certificate_id: certification.certificate_id,
        pro_id: certification.pro_id,
        status: certification.status,
      });
    },
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: getAdminGigsterProfilesListQueryKey() });
      queryClient.invalidateQueries({ queryKey: adminProQueryOptions(variables.pro_id).queryKey });
    },
  });
};
